import React from 'react';
import { useTranslation } from 'react-i18next';
import { SellerProductBasicDetails } from './types';
import TitleText from '../../atoms/TitleText';
import InfoText from '../../atoms/InfoText';
import { useGetCustomizationTypesQuery } from '../../../store/services/lookups';
import { useDispatch } from 'react-redux';
import { setModalId } from '../../../store/slices/publicSlice';

type Props = {
  basicDetails?: SellerProductBasicDetails;
};

export default function ProductOemSection({ basicDetails }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: supportTypes = [] } = useGetCustomizationTypesQuery();

  if (!basicDetails) return null;

  const hasOem =
    basicDetails.customizationTypes &&
    basicDetails.customizationTypes.length > 0;
  const hasLogo = !!basicDetails.logoImageUrl;

  if (!hasOem && !hasLogo) return null;

  const handlePreview = (url?: string) => {
    if (!url) return;
    const isImage = url.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i);
    const isVideo = url.match(/\.(mp4|webm|ogg)$/i);

    if (isImage || isVideo) {
      dispatch(
        setModalId({
          id: 'VIEW_FILE',
          extraData: { url, type: isImage ? 'IMAGE' : 'VIDEO' },
        })
      );
    } else {
      window.open(url, '_blank');
    }
  };

  // Filter the UUIDs to valid ones, and map to names
  const validTypes = (basicDetails.customizationTypes || [])
    .map((uuid) => supportTypes.find((st) => st.id === uuid))
    .filter((st) => st !== undefined);

  // Combine custom logo and types into one list so we can map them efficiently for zebra striping
  const listItems: {
    label: string;
    value: string;
    isActive: boolean;
    url?: string;
  }[] = [];

  if (hasLogo && basicDetails.logoImageUrl) {
    const fileName =
      basicDetails.logoImageUrl.split('/').pop() || 'Custom Logo.png';
    // try to get actual filename if available, or fallback
    // basicDetails doesn't store the exact original file name unfortunately
    listItems.push({
      label: t('customLogoArea'),
      value: fileName,
      isActive: false,
      url: basicDetails.logoImageUrl,
    });
  }

  validTypes.forEach((item) => {
    const mapping: Record<string, string> = {
      CUSTOM_LOGO: 'customLogo',
      CUSTOM_COLOR: 'customColor',
      CUSTOM_PACKAGING: 'customPackaging',
      OEM_YOUR_DESIGN: 'oemYourDesign',
      PRIVATE_LABEL: 'privateLabelYourText',
      CUSTOM_SIZE: 'customSize',
    };
    const camelKey = item!.code
      .toLowerCase()
      .replace(/_([a-z])/g, (g) => g[1].toUpperCase());
    const key = mapping[item!.code] || camelKey;
    const translatedName = t(key) || item!.name;

    // Don't duplicate Custom Logo if we already added it above!
    // Wait, CUSTOM_LOGO UUID is selected by the user. Let's just render the types.
    if (item!.code === 'CUSTOM_LOGO' && hasLogo) return; // Skip because handled above

    listItems.push({
      label: translatedName,
      value: t('active'),
      isActive: true,
    });
  });

  return (
    <div
      className="flex flex-col gap-6 w-full"
      data-testid="seller-product-oem-section"
    >
      <TitleText
        text={t('oemCustomization')}
        size="lg"
        className="!font-bold !text-[#090909] dark:!text-white"
      />

      <div className="flex flex-col" data-testid="seller-product-oem-list">
        {listItems.map((item, idx) => (
          <div
            key={idx}
            className="flex justify-between items-center px-3 py-4 text-sm even:bg-gray-50 dark:even:bg-gray-800/50"
            data-testid={`oem-row-${idx}`}
          >
            <InfoText
              text={item.label}
              className="!text-[#777777] dark:!text-gray-400 !text-sm !m-0"
            />
            {item.url ? (
              <div
                onClick={() => handlePreview(item.url)}
                className="cursor-pointer hover:underline"
              >
                <InfoText
                  text={item.value}
                  className="!text-sm !font-bold !m-0 !text-[#090909] dark:!text-white"
                />
              </div>
            ) : (
              <InfoText
                text={item.value}
                className={`!text-sm !font-bold !m-0 ${item.isActive ? '!text-light-success !font-semibold' : '!text-[#090909] dark:!text-white'}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
